import { httpErrorMessage, httpStatusOf, isTransportError } from '../plugins/http/policy.js'
import { OPTION_MAPPING_TEXT as T } from './deliveryMenuNotes.js'

/** 옵션 연결 화면의 순수 검색·선택 규칙. 대상을 이름 하나로 식별하지 않는다. */
export function optionTargetKey(target) {
  return target ? JSON.stringify([target.groupRef, target.itemRef || null, target.itemName]) : ''
}

export function matchesOptionSearch(values, query) {
  const words = String(query || '')
    .trim()
    .toLocaleLowerCase()
    .split(/\s+/)
    .filter(Boolean)
  const text = values
    .filter((v) => v != null)
    .join(' ')
    .toLocaleLowerCase()
  return words.every((word) => text.includes(word))
}

export function filterMappingLinks(links, platform, query) {
  return (links || []).filter(
    (link) =>
      (!platform || link.platform === platform) &&
      matchesOptionSearch(
        [link.menuName, link.platformMenuName, link.shopName, link.shopNo, link.catalogMenuRef].concat(
          (link.candidates || []).flatMap((c) => [c.groupName, c.target.itemName, c.price])
        ),
        query
      )
  )
}

/** 재조회는 현재 선택을 보존하고, 최초 진입은 실제로 지정한 메뉴·옵션 ID로만 찾는다. */
export function selectMappingLink(
  links,
  { selectedLinkId = null, initialSmenuCd = null, initialOptionCd = null } = {}
) {
  return (
    links.find((link) => selectedLinkId != null && link.linkId === selectedLinkId) ||
    links.find(
      (link) =>
        (initialSmenuCd != null && link.smenuCd === initialSmenuCd) ||
        (initialOptionCd != null && (link.options || []).some((option) => option.smenuOptCd === initialOptionCd))
    ) ||
    links[0] ||
    null
  )
}

/** 저장 receipt는 재조회 결과와 별개다. 후속/수동 재조회 실패가 원래 저장 결과를 덮지 않는다. */
export function mappingSaveFeedback(saveReceipt, refreshError, translate = (value) => value) {
  const refreshDetail = refreshError && !isTransportError(refreshError) ? httpErrorMessage(refreshError, '') : ''
  const withRefreshDetail = (message) => (refreshDetail ? `${message} ${refreshDetail}` : message)
  if (!saveReceipt) {
    return {
      message: refreshError ? withRefreshDetail(translate(T.LOAD_FAILED)) : '',
      type: refreshError ? 'error' : 'info',
    }
  }
  if (saveReceipt.ok) {
    return {
      message: refreshError ? withRefreshDetail(translate(T.SAVED_REFRESH_FAILED)) : translate(T.SAVED),
      type: refreshError ? 'error' : 'success',
    }
  }
  const status = httpStatusOf(saveReceipt.error)
  // 요청 거절(4xx)만 확정 실패다. 408·무응답·5xx는 커밋 뒤 응답이 유실됐을 수도 있다.
  const rejected = !isTransportError(saveReceipt.error) && status >= 400 && status < 500 && status !== 408
  const message = rejected ? httpErrorMessage(saveReceipt.error) : translate(T.SAVE_UNCERTAIN)
  return {
    message: refreshError
      ? withRefreshDetail(`${message} ${translate(rejected ? T.LOAD_FAILED : T.REFRESH_FAILED)}`)
      : message,
    type: 'error',
  }
}

export function canSelectOptionTarget(candidate, optionCd) {
  return !!candidate && candidate.selectable && (candidate.connectedOptionCds || []).every((cd) => cd === optionCd)
}

export function mappingTargetLabel(target, groupName) {
  const ids = [target.groupRef, target.itemRef].filter(Boolean).join(' / ')
  return `${groupName || target.groupRef} / ${target.itemName}${ids ? ` (#${ids})` : ''}`
}

function readMappingHistory(raw) {
  try {
    return JSON.parse(raw)
  } catch (_) {
    return null
  }
}

export function mappingHistoryTarget(raw) {
  const value = readMappingHistory(raw)
  if (!value) return '이력 확인 필요'
  return value.itemName ? mappingTargetLabel(value, value.groupName) : '연결 없음'
}

export function mappingHistorySource(raw, optionCd) {
  const value = readMappingHistory(raw)
  return value && value.ordernOption ? `${value.ordernGroup} / ${value.ordernOption}` : `#${optionCd}`
}
